import { useState, useRef } from 'react';
import { usePresignedUrl, useCreateEvidence } from '@/api/evidence';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

const MAX_SIZE = 50 * 1024 * 1024; // 50MB

export function EvidenceUploadDialog({
  lessonId,
  onClose,
  onCompleted,
}: {
  lessonId: number;
  onClose: () => void;
  onCompleted: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const presignedUrl = usePresignedUrl();
  const createEvidence = useCreateEvidence();

  async function handleUpload() {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      setErrorMsg('El archivo excede el limite de 50MB.');
      return;
    }

    setUploading(true);
    setErrorMsg('');

    try {
      // Step 1: Get presigned URL
      const res = await presignedUrl.mutateAsync({
        file_name: file.name,
        file_type: file.type || 'application/octet-stream',
        file_size: file.size,
        folder: 'evidence',
      });
      const { upload_url, download_url } = res.data;

      // Step 2: Upload to DO Spaces
      const uploadRes = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!uploadRes.ok) throw new Error('Upload to storage failed');

      // Step 3: Record evidence in API
      await createEvidence.mutateAsync({
        lesson_id: lessonId,
        file_url: download_url,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type || 'application/octet-stream',
        description: description || undefined,
      });

      // Step 4: Mark lesson as complete
      onCompleted();
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al subir el archivo.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <li className="border-t border-border bg-amber-50/50 px-5 py-4 dark:bg-amber-950/10">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-card-foreground">Subir Evidencia</p>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.pptx,.jpg,.jpeg,.png,.webp"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
        />

        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descripción (opcional)..."
          rows={2}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />

        {errorMsg && <p className="text-xs font-medium text-red-600">{errorMsg}</p>}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || uploading}
            className={cn(
              'rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90',
              (!file || uploading) && 'cursor-not-allowed opacity-60',
            )}
          >
            {uploading ? 'Subiendo...' : 'Subir y Completar'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-xs font-medium text-card-foreground hover:bg-accent"
          >
            Cancelar
          </button>
        </div>
      </div>
    </li>
  );
}
